import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { getReplyPreviewText } from '../lib/replyPreview';

const API = process.env.REACT_APP_BACKEND_URL;

export function useChatMessages(token, currentUserId) {
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  // Live total — derived from the conversation list so every WS new_message / read event updates badges instantly.
  const unreadTotal = useMemo(() => conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0), [conversations]);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);

  // Keep a live ref of the current messages so loadOlderMessages can read
  // the oldest loaded message without being re-created on every render.
  const messagesRef = useRef([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API}/api/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, [token]);

  // Fetch messages for a conversation
  // opts.around: when set, request a window centred on that message_id
  // so the global search can scroll-and-highlight messages that live
  // deeper than the latest 50.
  const fetchMessages = useCallback(async (conversationId, opts = {}) => {
    if (!token) return;

    try {
      const params = new URLSearchParams();
      if (opts.around) params.set('around', opts.around);
      if (opts.limit)  params.set('limit', String(opts.limit));
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`${API}/api/chat/conversations/${conversationId}/messages${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data);
        // A fresh load (not a cursor page) resets the older-history flag.
        if (!opts.before) setHasMoreOlder(true);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [token]);

  // Load an older page of history (cursor-style, before the oldest loaded
  // message) and PREPEND it, preserving the current view. Returns the number
  // of new messages prepended so the caller can restore scroll position.
  const loadOlderMessages = useCallback(async (conversationId) => {
    if (!token || !conversationId) return 0;
    const current = messagesRef.current;
    const oldest = current && current[0];
    if (!oldest || !oldest.message_id) return 0;
    setLoadingOlder(true);
    try {
      const response = await fetch(
        `${API}/api/chat/conversations/${conversationId}/messages?before=${encodeURIComponent(oldest.message_id)}&limit=30`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) return 0;
      const older = await response.json();
      if (!Array.isArray(older) || older.length === 0) {
        setHasMoreOlder(false);
        return 0;
      }
      let added = 0;
      setMessages(prev => {
        const existing = new Set(prev.map(m => m.message_id));
        const dedup = older.filter(m => !existing.has(m.message_id));
        added = dedup.length;
        return [...dedup, ...prev];
      });
      if (older.length < 30) setHasMoreOlder(false);
      return added;
    } catch (error) {
      console.error('Error loading older messages:', error);
      return 0;
    } finally {
      setLoadingOlder(false);
    }
  }, [token]);

  // Create conversation
  const createConversation = useCallback(async (participantIds, type = 'direct', name = null) => {
    if (!token) return null;
    
    try {
      const response = await fetch(`${API}/api/chat/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          participant_ids: participantIds,
          conversation_type: type,
          name
        })
      });
      
      if (response.ok) {
        const conversation = await response.json();
        await fetchConversations();
        return conversation;
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
    return null;
  }, [token, fetchConversations]);

  // Add message to state (for WebSocket updates)
  const removeMessage = useCallback((messageId) => {
    setMessages(prev => prev.filter(m => m.message_id !== messageId));
  }, []);

  const addMessage = useCallback((newMsg, activeConversationId) => {
    if (newMsg.conversation_id === activeConversationId) {
      setMessages(prev => {
        if (prev.some(m => m.message_id === newMsg.message_id)) return prev;
        return [...prev, newMsg];
      });
    }
    
    // Update conversation list
    setConversations(prev => prev.map(conv => {
      if (conv.conversation_id === newMsg.conversation_id) {
        return {
          ...conv,
          last_message: {
            content: newMsg.content,
            preview: getReplyPreviewText(newMsg),
            sender_name: newMsg.sender_name,
            created_at: newMsg.created_at
          },
          unread_count: (conv.conversation_id === activeConversationId || newMsg.sender_id === currentUserId)
            ? (conv.conversation_id === activeConversationId ? 0 : (conv.unread_count || 0))
            : (conv.unread_count || 0) + 1
        };
      }
      return conv;
    }));
  }, [currentUserId]);

  // Update message read status
  const markMessageRead = useCallback((messageId, readBy, readAt) => {
    setMessages(prev => prev.map(msg => {
      if (msg.message_id === messageId) {
        const rb = msg.read_by || [];
        return {
          ...msg,
          read_by: rb.includes(readBy) ? rb : [...rb, readBy],
          read_at: { ...(msg.read_at || {}), [readBy]: (msg.read_at || {})[readBy] || readAt || new Date().toISOString() }
        };
      }
      return msg;
    }));
  }, []);

  // Delivered receipt (recipient's device accepted the message over WebSocket)
  const markMessageDelivered = useCallback((messageId, deliveredTo, deliveredAt) => {
    setMessages(prev => prev.map(msg => {
      if (msg.message_id === messageId) {
        const cur = msg.delivered_to || [];
        const add = Array.isArray(deliveredTo) ? deliveredTo : [deliveredTo];
        return { ...msg, delivered_to: Array.from(new Set([...cur, ...add])), delivered_at: { ...(msg.delivered_at || {}), ...(deliveredAt || {}) } };
      }
      return msg;
    }));
  }, []);

  // Update message fields (e.g. transcription)
  const updateMessage = useCallback((messageId, updates) => {
    setMessages(prev => prev.map(msg => {
      if (msg.message_id === messageId) {
        return { ...msg, ...updates };
      }
      return msg;
    }));
  }, []);

  return {
    conversations,
    setConversations,
    messages,
    setMessages,
    unreadTotal,
    markMessageDelivered,
    fetchConversations,
    fetchMessages,
    loadOlderMessages,
    hasMoreOlder,
    loadingOlder,
    createConversation,
    addMessage,
    removeMessage,
    markMessageRead,
    updateMessage
  };
}
